import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ChevronDown, ChevronUp, Play, Check, Trash2, Plus, RefreshCcw,
} from "lucide-react";

const TABS = ["products", "vault", "buy", "requests", "trades", "orders", "history", "analytics"];
const QUEUE_REFRESH_INTERVAL_MS = 4000;

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("queue");

  if (user === null) {
    return <div className="text-center py-32 text-slate-400" data-testid="admin-loading">Checking session…</div>;
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">
        // Control room
      </div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">
        Admin Dashboard
      </h1>
      <p className="mt-2 text-slate-400">Hey {user.name} — manage your live show.</p>

      <div className="mt-8 flex gap-1 border-b border-slate-800">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm font-mono uppercase tracking-wider transition-colors ${
              tab === t
                ? "text-yellow-400 border-b-2 border-yellow-400"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "products" && <ProductsAdmin />}
        {tab === "vault" && <VaultAdmin />}
        {tab === "buy" && <BuyRequestsAdmin />}
        {tab === "requests" && <CardRequestsAdmin />}
        {tab === "trades" && <TradesAdmin />}
        {tab === "orders" && <OrdersAdmin mode="active" />}
        {tab === "history" && <OrdersAdmin mode="history" />}
        {tab === "analytics" && <AnalyticsAdmin />}
      </div>
    </div>
  );
}

// ---------- Queue admin ----------
function QueueAdmin() {
  const [queue, setQueue] = useState([]);
  const [form, setForm] = useState({ customer_name: "", product_name: "", fulfillment: "rip", note: "" });
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/queue");
      setQueue(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  /* eslint-disable */
  useEffect(() => {
    load();
    const id = setInterval(load, QUEUE_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  /* eslint-enable */

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/queue", form);
      setForm({ customer_name: "", product_name: "", fulfillment: "rip", note: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    await api.put(`/queue/${id}`, { status });
    load();
  };
  const move = async (id, direction) => {
    await api.post(`/queue/${id}/move?direction=${direction}`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this entry?")) return;
    await api.delete(`/queue/${id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={add} data-testid="admin-queue-form" className="lg:col-span-1 bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4 h-fit">
        <h2 className="font-bold text-white">Add to queue</h2>
        <input data-testid="qadmin-customer" required placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={input} />
        <input data-testid="qadmin-product" required placeholder="Product" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className={input} />
        <select data-testid="qadmin-fulfillment" value={form.fulfillment} onChange={(e) => setForm({ ...form, fulfillment: e.target.value })} className={input}>
          <option value="rip">Rip on stream</option>
          <option value="ship_sealed">Ship sealed</option>
        </select>
        <input placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} />
        {error && <div className="text-red-400 text-xs">{error}</div>}
        <button data-testid="qadmin-add" type="submit" className="w-full bg-yellow-400 text-slate-950 font-bold py-2.5 rounded-md uppercase text-sm">
          <Plus className="w-4 h-4 inline mr-1" /> Add
        </button>
      </form>

      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <h2 className="font-bold text-white">Live Queue ({queue.length})</h2>
          <button onClick={load} className="text-slate-400 hover:text-white" data-testid="qadmin-refresh">
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y divide-slate-800">
          {queue.length === 0 && <div className="p-6 text-slate-500 text-sm">Queue is empty.</div>}
          {queue.map((q, idx) => (
            <div key={q.id} data-testid={`qadmin-row-${q.id}`} className={`p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${q.status === "live" ? "bg-yellow-400/5 border-l-4 border-yellow-400" : ""}`}>
              <div className="font-mono text-slate-500 text-xs w-8">#{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{q.customer_name}</div>
                <div className="text-xs text-slate-500 truncate">{q.product_name} · {q.fulfillment === "rip" ? "Rip" : "Sealed"}</div>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <IconBtn testId={`qadmin-up-${q.id}`} onClick={() => move(q.id, "up")}><ChevronUp className="w-4 h-4" /></IconBtn>
                <IconBtn testId={`qadmin-down-${q.id}`} onClick={() => move(q.id, "down")}><ChevronDown className="w-4 h-4" /></IconBtn>
                <button data-testid={`qadmin-live-${q.id}`} onClick={() => setStatus(q.id, "live")} className={`px-2 py-1 text-[10px] font-mono uppercase rounded ${q.status === "live" ? "bg-yellow-400 text-slate-950" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
                  <Play className="w-3 h-3 inline" /> Live
                </button>
                <button data-testid={`qadmin-done-${q.id}`} onClick={() => setStatus(q.id, "done")} className="px-2 py-1 text-[10px] font-mono uppercase rounded bg-slate-800 text-slate-300 hover:bg-slate-700">
                  <Check className="w-3 h-3 inline" /> Done
                </button>
                <IconBtn testId={`qadmin-delete-${q.id}`} danger onClick={() => remove(q.id)}><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Products admin ----------
const blankProduct = {
  name: "", set_name: "", type: "Booster Pack", price: 0, stock: 0,
  image_url: "", description: "", rip_available: true, ship_available: true,
};

function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(blankProduct);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(blankProduct); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const cancel = () => { setEditing(null); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
      if (editing === "new") {
        await api.post("/products", payload);
      } else {
        await api.put(`/products/${editing}`, payload);
      }
      cancel();
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const resetCatalog = async () => {
    if (!window.confirm(
      "This will DELETE every product AND every single/slab in the vault. " +
      "Use this to wipe sample/demo data. This cannot be undone. Continue?"
    )) return;
    try {
      const { data } = await api.delete("/admin/catalog/reset");
      alert(`Catalog cleared: ${data.products_deleted} products and ${data.vault_deleted} vault items deleted.`);
      load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
        <h2 className="font-bold text-white">Products ({products.length})</h2>
        <div className="flex gap-2">
          <button
            data-testid="padmin-reset-catalog"
            onClick={resetCatalog}
            className="bg-red-700 text-white font-bold px-3 py-2 rounded-md text-xs uppercase hover:bg-red-800"
          >
            Reset catalog
          </button>
          <button data-testid="padmin-new" onClick={startNew} className="bg-yellow-400 text-slate-950 font-bold px-3 py-2 rounded-md text-xs uppercase">
            <Plus className="w-4 h-4 inline" /> New
          </button>
        </div>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="padmin-form" className="bg-slate-900 border border-yellow-400/40 rounded-xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="padmin-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          <input placeholder="Set name" value={form.set_name} onChange={(e) => setForm({ ...form, set_name: e.target.value })} className={input} />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={input}>
            {["Booster Pack", "Booster Bundle", "ETB", "Booster Box", "Collection Box", "Tin"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <input data-testid="padmin-price" required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} />
          <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={input} />
          <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={input} />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${input} sm:col-span-2`} rows={2} />
          {error && <div className="text-red-400 text-sm sm:col-span-2">{error}</div>}
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="padmin-save" type="submit" className="bg-yellow-400 text-slate-950 font-bold px-4 py-2 rounded uppercase text-sm">Save</button>
            <button type="button" onClick={cancel} className="bg-slate-800 text-white px-4 py-2 rounded text-sm">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Name</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Type</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Price</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Stock</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-slate-800/50">
                <td className="py-3 px-4 text-white">{p.name}</td>
                <td className="py-3 px-4 text-slate-400">{p.type}</td>
                <td className="py-3 px-4 text-yellow-400 font-mono">${p.price.toFixed(2)}</td>
                <td className="py-3 px-4 text-slate-400">{p.stock}</td>
                <td className="py-3 px-4 text-right">
                  <button data-testid={`padmin-edit-${p.id}`} onClick={() => startEdit(p)} className="text-yellow-400 hover:text-yellow-300 text-xs uppercase mr-3">Edit</button>
                  <button data-testid={`padmin-delete-${p.id}`} onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 text-xs uppercase">Delete</button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={5} className="py-8 text-center text-slate-500">No products yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Pulls admin ----------
const blankPull = {
  card_name: "",
  set_name: "",
  rarity: "Hit",
  image_url: "",
  pulled_for: "",
  estimated_value: 0,
  note: "",
};

function PullsAdmin() {
  const [pulls, setPulls] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankPull);
  const [error, setError] = useState("");
  const [featuredUrl, setFeaturedUrl] = useState("");
  const [featuredDraft, setFeaturedDraft] = useState("");
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [featuredSaved, setFeaturedSaved] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pulls");
    setPulls(data);
  };
  const loadSettings = async () => {
    const { data } = await api.get("/settings");
    setFeaturedUrl(data?.featured_tiktok_url || "");
    setFeaturedDraft(data?.featured_tiktok_url || "");
  };
  // eslint-disable-next-line
  useEffect(() => { load(); loadSettings(); }, []);

  const saveFeatured = async (e) => {
    e.preventDefault();
    setFeaturedSaving(true);
    setFeaturedSaved(false);
    try {
      const { data } = await api.patch("/settings", { featured_tiktok_url: featuredDraft.trim() });
      setFeaturedUrl(data?.featured_tiktok_url || "");
      setFeaturedDraft(data?.featured_tiktok_url || "");
      setFeaturedSaved(true);
      setTimeout(() => setFeaturedSaved(false), 2500);
    } finally {
      setFeaturedSaving(false);
    }
  };
  const clearFeatured = async () => {
    setFeaturedSaving(true);
    try {
      await api.patch("/settings", { featured_tiktok_url: "" });
      setFeaturedUrl("");
      setFeaturedDraft("");
    } finally {
      setFeaturedSaving(false);
    }
  };

  const startNew = () => { setEditing("new"); setForm(blankPull); };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const cancel = () => { setEditing(null); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 };
      if (editing === "new") {
        await api.post("/pulls", payload);
      } else {
        await api.put(`/pulls/${editing}`, payload);
      }
      cancel();
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this pull?")) return;
    await api.delete(`/pulls/${id}`);
    load();
  };

  return (
    <div>
      {/* Featured TikTok URL */}
      <form
        onSubmit={saveFeatured}
        data-testid="featured-tiktok-form"
        className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-6"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-white text-sm">Featured TikTok on Pulls page</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Paste a TikTok video URL to spotlight it at the top of the Pulls page.
              Format: <span className="font-mono text-slate-400">https://www.tiktok.com/@handle/video/1234567890</span>
            </p>
          </div>
          {featuredUrl && (
            <a
              href={featuredUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] font-mono uppercase tracking-wider text-yellow-400 hover:text-yellow-300 whitespace-nowrap"
              data-testid="featured-tiktok-open"
            >
              Open ↗
            </a>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="featured-tiktok-input"
            type="url"
            placeholder="https://www.tiktok.com/@washeduptrainerss/video/..."
            value={featuredDraft}
            onChange={(e) => setFeaturedDraft(e.target.value)}
            className={`${input} flex-1`}
          />
          <button
            type="submit"
            data-testid="featured-tiktok-save"
            disabled={featuredSaving || featuredDraft.trim() === featuredUrl}
            className="bg-yellow-400 text-slate-950 font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wide hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {featuredSaving ? "Saving…" : featuredSaved ? "Saved ✓" : "Save"}
          </button>
          {featuredUrl && (
            <button
              type="button"
              data-testid="featured-tiktok-clear"
              onClick={clearFeatured}
              disabled={featuredSaving}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-3 py-2 rounded-md text-xs uppercase tracking-wide disabled:opacity-40"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold text-white">Recent Pulls ({pulls.length})</h2>
        <button data-testid="pulladmin-new" onClick={startNew} className="bg-yellow-400 text-slate-950 font-bold px-3 py-2 rounded-md text-xs uppercase">
          <Plus className="w-4 h-4 inline" /> Add pull
        </button>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="pulladmin-form" className="bg-slate-900 border border-yellow-400/40 rounded-xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="pulladmin-name" required placeholder="Card name (e.g. Charizard ex)" value={form.card_name} onChange={(e) => setForm({ ...form, card_name: e.target.value })} className={input} />
          <input placeholder="Set name" value={form.set_name} onChange={(e) => setForm({ ...form, set_name: e.target.value })} className={input} />
          <select value={form.rarity} onChange={(e) => setForm({ ...form, rarity: e.target.value })} className={input}>
            {["Hit", "Holo", "Reverse", "Full Art", "Alt Art", "Secret", "Gold", "Rainbow"].map((r) => <option key={r}>{r}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Est. value ($)" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className={input} />
          <input data-testid="pulladmin-image" required placeholder="Image URL (https://…)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={`${input} sm:col-span-2`} />
          <input placeholder="Pulled for (customer name, optional)" value={form.pulled_for} onChange={(e) => setForm({ ...form, pulled_for: e.target.value })} className={`${input} sm:col-span-2`} />
          <textarea placeholder="Note / story (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${input} sm:col-span-2`} rows={2} />
          {error && <div className="text-red-400 text-sm sm:col-span-2">{error}</div>}
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="pulladmin-save" type="submit" className="bg-yellow-400 text-slate-950 font-bold px-4 py-2 rounded uppercase text-sm">Save</button>
            <button type="button" onClick={cancel} className="bg-slate-800 text-white px-4 py-2 rounded text-sm">Cancel</button>
          </div>
        </form>
      )}

      {pulls.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center text-slate-500">
          No pulls posted yet. Add your first hit above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {pulls.map((p) => (
            <div key={p.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="aspect-[3/4] bg-slate-950 overflow-hidden">
                <img src={p.image_url} alt={p.card_name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-1">
                <div className="text-[10px] font-mono uppercase tracking-wider text-yellow-400">{p.rarity}</div>
                <div className="text-white text-sm font-bold leading-tight line-clamp-1">{p.card_name}</div>
                <div className="text-[10px] text-slate-500">{p.set_name}</div>
                <div className="flex gap-2 pt-2">
                  <button data-testid={`pulladmin-edit-${p.id}`} onClick={() => startEdit(p)} className="text-yellow-400 hover:text-yellow-300 text-[10px] uppercase font-mono">Edit</button>
                  <button data-testid={`pulladmin-delete-${p.id}`} onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 text-[10px] uppercase font-mono">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Vault admin ----------
const blankVault = {
  item_type: "slab",
  card_name: "",
  set_name: "",
  grading_company: "PSA",
  grade: "10",
  cert_number: "",
  price: 0,
  front_image_url: "",
  back_image_url: "",
  extra_images: [],
  description: "",
  sold: false,
  blur_cert_in_photo: true,
};

function VaultAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankVault);
  const [error, setError] = useState("");
  const [extraInput, setExtraInput] = useState("");

  const load = async () => {
    const { data } = await api.get("/vault?include_sold=true");
    setItems(data);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(blankVault); setExtraInput(""); };
  const startEdit = (it) => { setEditing(it.id); setForm({ ...blankVault, ...it }); setExtraInput(""); };
  const cancel = () => { setEditing(null); setError(""); };

  const addExtra = () => {
    const url = extraInput.trim();
    if (!url) return;
    setForm((f) => ({ ...f, extra_images: [...(f.extra_images || []), url] }));
    setExtraInput("");
  };
  const removeExtra = (i) => {
    setForm((f) => ({ ...f, extra_images: f.extra_images.filter((_, idx) => idx !== i) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form, price: Number(form.price) || 0 };
      if (editing === "new") {
        await api.post("/vault", payload);
      } else {
        await api.put(`/vault/${editing}`, payload);
      }
      cancel();
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vault item?")) return;
    await api.delete(`/vault/${id}`);
    load();
  };

  const toggleSold = async (it) => {
    await api.put(`/vault/${it.id}`, { ...it, sold: !it.sold });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold text-white">TCG Vault ({items.length})</h2>
        <button data-testid="vadmin-new" onClick={startNew} className="bg-yellow-400 text-slate-950 font-bold px-3 py-2 rounded-md text-xs uppercase">
          <Plus className="w-4 h-4 inline" /> Add slab / single
        </button>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="vadmin-form" className="bg-slate-900 border border-yellow-400/40 rounded-xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select data-testid="vadmin-type" value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} className={input}>
            <option value="slab">Slab</option>
            <option value="single">Single (raw)</option>
          </select>
          <input data-testid="vadmin-name" required placeholder="Card name (e.g. Charizard ex)" value={form.card_name} onChange={(e) => setForm({ ...form, card_name: e.target.value })} className={input} />
          <input placeholder="Set name" value={form.set_name} onChange={(e) => setForm({ ...form, set_name: e.target.value })} className={input} />
          <input data-testid="vadmin-price" required type="number" step="0.01" placeholder="Price (CAD)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} />
          <select value={form.grading_company} onChange={(e) => setForm({ ...form, grading_company: e.target.value })} className={input}>
            {["", "PSA", "BGS", "CGC", "Raw"].map((g) => <option key={g} value={g}>{g || "— none —"}</option>)}
          </select>
          <input placeholder="Grade (e.g. 10, 9.5, NM)" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className={input} />
          <input data-testid="vadmin-cert" placeholder="Cert number (if graded)" value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} className={`${input} sm:col-span-2`} />
          <input data-testid="vadmin-front" placeholder="Front image URL" value={form.front_image_url} onChange={(e) => setForm({ ...form, front_image_url: e.target.value })} className={input} />
          <input data-testid="vadmin-back" placeholder="Back image URL" value={form.back_image_url} onChange={(e) => setForm({ ...form, back_image_url: e.target.value })} className={input} />

          {/* Extra images */}
          <div className="sm:col-span-2 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Extra detail photos</div>
            <div className="flex gap-2">
              <input placeholder="Paste detail image URL" value={extraInput} onChange={(e) => setExtraInput(e.target.value)} className={`${input} flex-1`} />
              <button type="button" onClick={addExtra} className="bg-slate-800 text-white px-3 rounded text-xs uppercase">Add</button>
            </div>
            {(form.extra_images || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.extra_images.map((url, i) => (
                  <div key={i} className="relative">
                    <img src={url} alt="" className="w-16 h-20 object-cover rounded border border-slate-800" />
                    <button type="button" onClick={() => removeExtra(i)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs leading-none">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <textarea placeholder="Description / story (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${input} sm:col-span-2`} rows={2} />
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input type="checkbox" checked={form.sold} onChange={(e) => setForm({ ...form, sold: e.target.checked })} className="w-4 h-4 accent-yellow-400" />
            Sold (hide from public listing)
          </label>

          {error && <div className="text-red-400 text-sm sm:col-span-2">{error}</div>}
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="vadmin-save" type="submit" className="bg-yellow-400 text-slate-950 font-bold px-4 py-2 rounded uppercase text-sm">Save</button>
            <button type="button" onClick={cancel} className="bg-slate-800 text-white px-4 py-2 rounded text-sm">Cancel</button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center text-slate-500">
          No vault items yet. Add your first slab or single above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} className={`bg-slate-900 border rounded-xl overflow-hidden ${it.sold ? "border-slate-700 opacity-60" : "border-slate-800"}`}>
              <div className="aspect-[3/4] bg-slate-950 overflow-hidden">
                {it.front_image_url ? (
                  <img src={it.front_image_url} alt={it.card_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-700 text-xs">No image</div>
                )}
              </div>
              <div className="p-3 space-y-1">
                <div className="text-[10px] font-mono uppercase tracking-wider text-yellow-400">
                  {it.item_type} {it.grading_company} {it.grade}
                </div>
                <div className="text-white text-sm font-bold leading-tight line-clamp-1">{it.card_name}</div>
                <div className="text-[10px] text-slate-500">{it.set_name}</div>
                <div className="text-sm text-yellow-400 font-mono font-bold">${it.price.toFixed(2)}</div>
                <div className="flex gap-2 pt-2 flex-wrap">
                  <button onClick={() => startEdit(it)} className="text-yellow-400 hover:text-yellow-300 text-[10px] uppercase font-mono" data-testid={`vadmin-edit-${it.id}`}>Edit</button>
                  <button onClick={() => toggleSold(it)} className="text-slate-300 hover:text-white text-[10px] uppercase font-mono" data-testid={`vadmin-sold-${it.id}`}>{it.sold ? "Mark unsold" : "Mark sold"}</button>
                  <button onClick={() => remove(it.id)} className="text-red-400 hover:text-red-300 text-[10px] uppercase font-mono" data-testid={`vadmin-delete-${it.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Buy Requests admin ----------
function BuyRequestsAdmin() {
  const [requests, setRequests] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const { data } = await api.get("/buy-requests");
    setRequests(data);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/buy-requests/${id}`, { status });
    load();
  };
  const saveNotes = async (id, admin_notes) => {
    await api.patch(`/buy-requests/${id}`, { admin_notes });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this buy request?")) return;
    await api.delete(`/buy-requests/${id}`);
    load();
  };

  const statusColor = {
    new: "bg-blue-500/20 text-blue-400 border-blue-500/40",
    contacted: "bg-yellow-400/20 text-yellow-400 border-yellow-400/40",
    accepted: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    declined: "bg-red-500/20 text-red-400 border-red-500/40",
    completed: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  };

  return (
    <div className="space-y-4" data-testid="buyadmin">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-white">Buy Requests ({requests.length})</h2>
      </div>
      {requests.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center text-slate-500">
          No buy requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} data-testid={`buyadmin-row-${r.id}`} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold">{r.customer_name}</div>
                  <div className="text-xs text-slate-500 break-all">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                  <div className="text-[10px] text-slate-600 font-mono mt-1">
                    {r.created_at.slice(0, 10)} · {r.items.length} item{r.items.length === 1 ? "" : "s"} · Wants{" "}
                    <span className={r.preference === "trade_credit" ? "text-yellow-400" : "text-emerald-400"}>
                      {r.preference === "trade_credit" ? "trade credit (80%)" : "cash (70–75%)"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`buyadmin-status-${r.id}`}
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className={`text-xs font-mono uppercase px-2 py-1 rounded border bg-transparent ${statusColor[r.status] || ""}`}
                  >
                    {["new", "contacted", "accepted", "declined", "completed"].map((s) => (
                      <option key={s} value={s} className="bg-slate-900">{s}</option>
                    ))}
                  </select>
                  <button
                    data-testid={`buyadmin-expand-${r.id}`}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className="text-xs font-mono uppercase text-yellow-400 hover:text-yellow-300"
                  >
                    {expanded === r.id ? "Close" : "View"}
                  </button>
                  <button
                    data-testid={`buyadmin-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="text-xs font-mono uppercase text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {expanded === r.id && (
                <div className="border-t border-slate-800 p-4 space-y-4 bg-slate-950">
                  {r.message && (
                    <div className="text-xs text-slate-300 bg-slate-900 border border-slate-800 rounded p-3">
                      <div className="text-[9px] font-mono uppercase text-slate-500 mb-1">Customer note</div>
                      {r.message}
                    </div>
                  )}
                  <div className="space-y-3">
                    {r.items.map((it, i) => (
                      <div key={i} data-testid={`buyadmin-item-${r.id}-${i}`} className="bg-slate-900 border border-slate-800 rounded p-3">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2">
                          <div>
                            <div className="text-white font-semibold">
                              {it.card_name} <span className="text-slate-500 font-normal">×{it.quantity}</span>
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {[it.set_name, it.condition].filter(Boolean).join(" · ")}
                            </div>
                            {it.asking_price > 0 && (
                              <div className="text-xs text-yellow-400 font-mono mt-1">Asking ${it.asking_price.toFixed(2)}</div>
                            )}
                            {it.notes && <div className="text-xs text-slate-500 mt-1 italic">{it.notes}</div>}
                          </div>
                        </div>
                        {it.photo_urls?.length > 0 && (
                          <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {it.photo_urls.map((url, pi) => (
                              <a
                                key={pi}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`buyadmin-photo-${r.id}-${i}-${pi}`}
                                className="aspect-square block bg-slate-950 border border-slate-800 rounded overflow-hidden hover:border-yellow-400/60"
                              >
                                <img src={url} alt={`${it.card_name} ${pi + 1}`} className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <AdminNotesField
                    testId={`buyadmin-notes-${r.id}`}
                    initial={r.admin_notes || ""}
                    onSave={(v) => saveNotes(r.id, v)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminNotesField({ initial, onSave, testId }) {
  const [v, setV] = React.useState(initial);
  const [saved, setSaved] = React.useState(false);
  React.useEffect(() => { setV(initial); }, [initial]);
  const save = async () => {
    await onSave(v);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div>
      <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">Admin notes (private)</div>
      <textarea
        data-testid={testId}
        rows={2}
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="w-full bg-slate-950 border border-slate-800 rounded p-2 text-xs text-white focus:outline-none focus:border-yellow-400"
      />
      <button
        type="button"
        onClick={save}
        data-testid={`${testId}-save`}
        className="mt-2 bg-yellow-400 text-slate-950 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded hover:bg-yellow-300"
      >
        {saved ? "Saved ✓" : "Save notes"}
      </button>
    </div>
  );
}

// ---------- Card Requests admin (customer wishlists) ----------
function CardRequestsAdmin() {
  const [requests, setRequests] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const { data } = await api.get("/card-requests");
    setRequests(data);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/card-requests/${id}`, { status });
    load();
  };
  const saveNotes = async (id, admin_notes) => {
    await api.patch(`/card-requests/${id}`, { admin_notes });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this card request?")) return;
    await api.delete(`/card-requests/${id}`);
    load();
  };

  const statusColor = {
    new: "bg-blue-500/20 text-blue-400 border-blue-500/40",
    contacted: "bg-yellow-400/20 text-yellow-400 border-yellow-400/40",
    sourced: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    completed: "bg-slate-500/20 text-slate-300 border-slate-500/40",
    closed: "bg-red-500/20 text-red-400 border-red-500/40",
  };

  return (
    <div className="space-y-4" data-testid="cardreqadmin">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-white">Card Requests ({requests.length})</h2>
      </div>
      {requests.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center text-slate-500">
          No card requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} data-testid={`cardreqadmin-row-${r.id}`} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold">{r.customer_name}</div>
                  <div className="text-xs text-slate-500 break-all">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                  <div className="text-[10px] text-slate-600 font-mono mt-1">
                    {r.created_at.slice(0, 10)} · {r.items.length} card{r.items.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`cardreqadmin-status-${r.id}`}
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className={`text-xs font-mono uppercase px-2 py-1 rounded border bg-transparent ${statusColor[r.status] || ""}`}
                  >
                    {["new", "contacted", "sourced", "completed", "closed"].map((s) => (
                      <option key={s} value={s} className="bg-slate-900">{s}</option>
                    ))}
                  </select>
                  <button
                    data-testid={`cardreqadmin-expand-${r.id}`}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className="text-xs font-mono uppercase text-yellow-400 hover:text-yellow-300"
                  >
                    {expanded === r.id ? "Close" : "View"}
                  </button>
                  <button
                    data-testid={`cardreqadmin-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="text-xs font-mono uppercase text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              </div>
              {expanded === r.id && (
                <div className="border-t border-slate-800 p-4 space-y-4 bg-slate-950">
                  {r.message && (
                    <div className="text-xs text-slate-300 bg-slate-900 border border-slate-800 rounded p-3">
                      <div className="text-[9px] font-mono uppercase text-slate-500 mb-1">Customer note</div>
                      {r.message}
                    </div>
                  )}
                  <div className="space-y-2">
                    {r.items.map((it, i) => (
                      <div key={i} data-testid={`cardreqadmin-item-${r.id}-${i}`} className="bg-slate-900 border border-slate-800 rounded p-3">
                        <div className="text-white font-semibold">
                          {it.card_name} <span className="text-slate-500 font-normal">×{it.quantity}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {[it.set_name, it.condition].filter(Boolean).join(" · ")}
                        </div>
                        {it.max_budget > 0 && (
                          <div className="text-xs text-yellow-400 font-mono mt-1">Max budget ${it.max_budget.toFixed(2)}</div>
                        )}
                        {it.notes && <div className="text-xs text-slate-500 mt-1 italic">{it.notes}</div>}
                      </div>
                    ))}
                  </div>
                  <AdminNotesField
                    testId={`cardreqadmin-notes-${r.id}`}
                    initial={r.admin_notes || ""}
                    onSave={(v) => saveNotes(r.id, v)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Trades admin ----------
function TradesAdmin() {
  const [trades, setTrades] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const { data } = await api.get("/trades");
    setTrades(data);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/trades/${id}`, { status });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this trade request?")) return;
    await api.delete(`/trades/${id}`);
    load();
  };

  const statusColor = {
    new: "bg-blue-500/20 text-blue-400 border-blue-500/40",
    reviewing: "bg-yellow-400/20 text-yellow-400 border-yellow-400/40",
    matched: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    closed: "bg-slate-500/20 text-slate-400 border-slate-500/40",
  };

  if (trades.length === 0) {
    return <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center text-slate-500">No trade requests yet.</div>;
  }

  return (
    <div className="space-y-3">
      {trades.map((t) => (
        <div key={t.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            {t.image_url && (
              <img src={t.image_url} alt={t.card_name} className="w-16 h-20 object-cover rounded border border-slate-800" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold">{t.card_name}</div>
              <div className="text-xs text-slate-500">
                {t.set_name} · {t.condition}{t.cert_number ? ` · Cert ${t.cert_number}` : ""}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                <span className="text-yellow-400">${(t.estimated_value || 0).toFixed(2)}</span> · wants:{" "}
                <span className="text-slate-300">{t.looking_for || "—"}</span>
              </div>
              <div className="text-[10px] text-slate-600 font-mono mt-1">{t.owner_name} · {t.owner_email}{t.owner_phone ? ` · ${t.owner_phone}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid={`tadmin-status-${t.id}`}
                value={t.status}
                onChange={(e) => setStatus(t.id, e.target.value)}
                className={`text-[10px] font-mono uppercase px-2 py-1 rounded border bg-transparent ${statusColor[t.status] || ""}`}
              >
                {["new", "reviewing", "matched", "closed"].map((s) => (
                  <option key={s} value={s} className="bg-slate-900">{s}</option>
                ))}
              </select>
              <button onClick={() => setExpanded(expanded === t.id ? null : t.id)} className="text-yellow-400 text-[10px] uppercase font-mono">
                {expanded === t.id ? "Hide" : "Details"}
              </button>
              <button data-testid={`tadmin-delete-${t.id}`} onClick={() => remove(t.id)} className="text-red-400 hover:text-red-300 text-[10px] uppercase font-mono">Delete</button>
            </div>
          </div>
          {expanded === t.id && (
            <div className="border-t border-slate-800 px-4 py-3 bg-slate-950 text-sm text-slate-300 space-y-1">
              <div><span className="text-slate-500 text-xs font-mono uppercase">Notes:</span> {t.notes || "—"}</div>
              <div className="text-xs text-slate-500 font-mono">Trade ID: {t.id} · {t.created_at.slice(0, 19).replace("T", " ")}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Orders admin ----------
const ACTIVE_STATUSES = ["new", "contacted", "queued"];
const HISTORY_STATUSES = ["completed", "cancelled"];

function OrdersAdmin({ mode = "active" }) {
  const [orders, setOrders] = useState([]);
  const [queuingId, setQueuingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/orders");
    setOrders(data);
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const buildQueueProductName = (order) => {
    const items = (order.items && order.items.length > 0)
      ? order.items
      : [{ product_name: order.product_name, quantity: order.quantity }];
    if (items.length === 1) {
      const it = items[0];
      return `${it.product_name}${it.quantity > 1 ? ` ×${it.quantity}` : ""}`;
    }
    return items.map((it) => `${it.product_name}${it.quantity > 1 ? ` ×${it.quantity}` : ""}`).join(" + ");
  };

  const setStatus = async (id, status) => {
    const order = orders.find((x) => x.id === id);
    // Auto-add to live queue when transitioning to "queued" for the first time
    if (status === "queued" && order && order.status !== "queued") {
      setQueuingId(id);
      try {
        await api.post("/queue", {
          customer_name: order.customer_name,
          product_name: buildQueueProductName(order),
          fulfillment: order.fulfillment,
          note: order.needs_shipping ? "Needs shipping" : "",
        });
      } catch (e) {
        console.error("[orders] queue add failed:", e);
      } finally {
        setQueuingId(null);
      }
    }
    await api.patch(`/orders/${id}`, { status });
    load();
  };
  const updatePricing = async (id, patch) => {
    await api.patch(`/orders/${id}`, patch);
    load();
  };
  const updateItemPrice = async (order, idx, newPrice) => {
    const items = (order.items && order.items.length > 0)
      ? order.items.map((it) => ({ ...it }))
      : [{
          product_id: order.product_id || "",
          product_name: order.product_name,
          quantity: order.quantity,
          unit_price: order.unit_price || 0,
        }];
    items[idx] = { ...items[idx], unit_price: newPrice };
    await api.patch(`/orders/${order.id}`, { items });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete order?")) return;
    await api.delete(`/orders/${id}`);
    load();
  };
  const restore = async (id) => setStatus(id, "new");
  const addToQueue = (o) => setStatus(o.id, "queued");

  const statusColor = {
    new: "bg-blue-500/20 text-blue-400 border-blue-500/40",
    contacted: "bg-yellow-400/20 text-yellow-400 border-yellow-400/40",
    queued: "bg-purple-500/20 text-purple-400 border-purple-500/40",
    completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    cancelled: "bg-red-500/20 text-red-400 border-red-500/40",
  };

  const allowedStatuses = mode === "history" ? HISTORY_STATUSES : ACTIVE_STATUSES;
  const filtered = orders.filter((o) => allowedStatuses.includes(o.status));

  const emptyLabel = mode === "history"
    ? "No completed or cancelled orders yet."
    : "No active orders yet.";

  // History summary stats
  const currentYear = new Date().getFullYear();
  const completedCount = orders.filter((o) => o.status === "completed").length;
  const cancelledCount = orders.filter((o) => o.status === "cancelled").length;
  const lifetimeRevenue = orders
    .filter((o) => o.status === "completed")
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const ytdRevenue = orders
    .filter((o) => o.status === "completed" && (o.created_at || "").startsWith(`${currentYear}-`))
    .reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="space-y-4" data-testid={`oadmin-${mode}`}>
      {mode === "history" && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <HistoryStat label="Completed orders" value={completedCount} testId="history-completed-count" />
          <HistoryStat label="Cancelled orders" value={cancelledCount} testId="history-cancelled-count" />
          <HistoryStat
            label={`Revenue ${currentYear} YTD`}
            value={`$${ytdRevenue.toFixed(2)}`}
            highlight
            testId="history-ytd-revenue"
          />
          <HistoryStat
            label="Lifetime revenue"
            value={`$${lifetimeRevenue.toFixed(2)}`}
            testId="history-lifetime-revenue"
          />
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Customer</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Items</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Type</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Ship $</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Total</th>
              <th className="py-3 px-4 font-mono text-xs text-slate-400 uppercase">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => {
              const items = (o.items && o.items.length > 0)
                ? o.items
                : [{
                    product_id: o.product_id || "",
                    product_name: o.product_name,
                    quantity: o.quantity,
                    unit_price: o.unit_price || 0,
                  }];
              return (
                <tr key={o.id} className="border-b border-slate-800/50 align-top">
                  <td className="py-3 px-4 max-w-xs">
                    <div className="text-white font-semibold">{o.customer_name}</div>
                    <div className="text-xs text-slate-500 break-all">{o.email}</div>
                    {o.phone && (
                      <div className="text-xs text-slate-500 font-mono mt-0.5" data-testid={`oadmin-phone-${o.id}`}>
                        {o.phone}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-600 font-mono mt-1">{o.created_at.slice(0, 10)}</div>
                    {o.needs_shipping && o.shipping_address && (
                      <div className="mt-2 bg-slate-950 border border-yellow-400/30 rounded-md p-2">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[9px] font-mono uppercase tracking-wider text-yellow-400">Ship to</span>
                          <button
                            type="button"
                            data-testid={`oadmin-copyaddr-${o.id}`}
                            onClick={() => navigator.clipboard?.writeText(`${o.customer_name}\n${o.shipping_address}`)}
                            className="text-[9px] font-mono uppercase text-slate-400 hover:text-yellow-400"
                          >
                            Copy
                          </button>
                        </div>
                        <div
                          data-testid={`oadmin-shipaddr-${o.id}`}
                          className="text-xs text-slate-200 whitespace-pre-wrap break-words leading-snug"
                        >
                          {o.shipping_address}
                        </div>
                      </div>
                    )}
                    {o.needs_shipping && !o.shipping_address && (
                      <div className="mt-2 text-[10px] font-mono uppercase text-red-400">
                        Ship requested · address missing
                      </div>
                    )}
                    {o.message && (
                      <div className="mt-2 bg-slate-950 border border-slate-800 rounded-md p-2">
                        <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1">Note</div>
                        <div className="text-xs text-slate-300 whitespace-pre-wrap break-words leading-snug">{o.message}</div>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1.5">
                      {items.map((it, idx) => (
                        <div
                          key={idx}
                          data-testid={`oadmin-item-${o.id}-${idx}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="text-slate-200 flex-1">
                            {it.product_name} <span className="text-slate-500">×{it.quantity}</span>
                          </span>
                          <PriceInput
                            value={it.unit_price || 0}
                            onSave={(v) => updateItemPrice(o, idx, v)}
                            testId={`oadmin-itemprice-${o.id}-${idx}`}
                            readOnly={mode === "history"}
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-xs">
                    {o.fulfillment === "rip" ? "Rip" : "Sealed"}
                    {o.needs_shipping ? " + Ship" : ""}
                  </td>
                  <td className="py-3 px-4">
                    <PriceInput
                      value={o.shipping_cost}
                      onSave={(v) => updatePricing(o.id, { shipping_cost: v })}
                      testId={`oadmin-ship-${o.id}`}
                      readOnly={mode === "history"}
                    />
                  </td>
                  <td className="py-3 px-4 font-mono font-bold text-yellow-400 tabular-nums whitespace-nowrap">
                    ${(o.total || 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    <select
                      data-testid={`oadmin-status-${o.id}`}
                      value={o.status}
                      onChange={(e) => setStatus(o.id, e.target.value)}
                      className={`text-xs font-mono uppercase px-2 py-1 rounded border bg-transparent ${statusColor[o.status] || ""}`}
                    >
                      {["new", "contacted", "queued", "completed", "cancelled"].map((s) => (
                        <option key={s} value={s} className="bg-slate-900">{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end items-center gap-3">
                      {mode === "active" ? (
                        <button
                          data-testid={`oadmin-queue-${o.id}`}
                          onClick={() => addToQueue(o)}
                          disabled={queuingId === o.id || o.status === "queued"}
                          title={o.status === "queued" ? "Already added to queue" : "Add this customer to the live queue"}
                          className="inline-flex items-center gap-1 bg-yellow-400 text-slate-950 font-bold text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Play className="w-3 h-3" />
                          {queuingId === o.id ? "Adding…" : o.status === "queued" ? "Queued" : "Queue"}
                        </button>
                      ) : (
                        <button
                          data-testid={`oadmin-restore-${o.id}`}
                          onClick={() => restore(o.id)}
                          className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-white font-bold text-[10px] uppercase tracking-wider px-2.5 py-1.5 rounded"
                          title="Move back to active orders"
                        >
                          <RefreshCcw className="w-3 h-3" /> Restore
                        </button>
                      )}
                      <button data-testid={`oadmin-delete-${o.id}`} onClick={() => remove(o.id)} className="text-red-400 hover:text-red-300 text-xs uppercase">Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-500">{emptyLabel}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HistoryStat({ label, value, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-slate-900 border rounded-xl p-4 ${highlight ? "border-yellow-400/40" : "border-slate-800"}`}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-black tabular-nums ${highlight ? "text-yellow-400" : "text-white"}`}>{value}</div>
    </div>
  );
}

// ---------- Analytics admin ----------
function AnalyticsAdmin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/stats");
      setStats(data);
    } catch (_) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  if (loading && !stats) {
    return <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center text-slate-500">Loading analytics…</div>;
  }
  if (!stats) {
    return <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center text-slate-500">No analytics data yet.</div>;
  }

  const maxDaily = Math.max(1, ...(stats.daily || []).map((d) => d.views));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-white text-lg">Site traffic</h2>
        <button onClick={load} className="text-xs text-yellow-400 hover:text-yellow-300 font-mono uppercase">
          <RefreshCcw className="w-3.5 h-3.5 inline mr-1" /> Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Today" views={stats.views_today} uniques={stats.unique_today} accent="yellow" />
        <Kpi label="Last 7 days" views={stats.views_week} uniques={stats.unique_week} />
        <Kpi label="Last 30 days" views={stats.views_month} uniques={stats.unique_month} />
        <Kpi label="All time" views={stats.total_views} uniques={stats.unique_total} />
      </div>

      {/* Daily chart */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h3 className="text-sm font-bold text-white mb-4">Last 14 days</h3>
        {(stats.daily || []).length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">No traffic logged yet — share the site!</div>
        ) : (
          <div className="flex items-end gap-2 h-48">
            {stats.daily.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5 group">
                <div className="text-[10px] text-slate-500 font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                  {d.views}
                </div>
                <div
                  className="w-full bg-yellow-400 rounded-t group-hover:bg-yellow-300 transition-colors"
                  style={{ height: `${(d.views / maxDaily) * 100}%`, minHeight: "2px" }}
                  title={`${d.date}: ${d.views} views · ${d.unique_visitors} unique`}
                />
                <div className="text-[9px] text-slate-600 font-mono">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top pages */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Top pages (last 30 days)</h3>
          <span className="text-[10px] text-slate-500 font-mono">PATH · VIEWS · UNIQUES</span>
        </div>
        {(stats.top_pages || []).length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No page data yet.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {stats.top_pages.map((p) => {
              const max = stats.top_pages[0].views || 1;
              const pct = (p.views / max) * 100;
              return (
                <div key={p.path} className="px-6 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <code className="text-yellow-400 font-mono text-sm truncate">{p.path}</code>
                    <span className="text-slate-300 font-mono text-sm tabular-nums">
                      {p.views} <span className="text-slate-600">·</span>{" "}
                      <span className="text-slate-500">{p.unique_visitors}</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-950 rounded overflow-hidden">
                    <div className="h-full bg-yellow-400/60" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Visits from <code className="bg-slate-900 px-1.5 py-0.5 rounded">/admin</code> are excluded so your own activity doesn&apos;t inflate numbers.
        Unique visitors counted per-browser via local session ID.
      </p>

      <LegacyAdminCleanupCard />
    </div>
  );
}

function LegacyAdminCleanupCard() {
  const [status, setStatus] = React.useState("idle"); // idle | running | done | error
  const [result, setResult] = React.useState(null);

  const run = async () => {
    if (!window.confirm("Delete the 3 legacy admin accounts (washeduptrainers@gmail.com, nunthranadenarong@gmail.com, cgalbraith93@gmail.com)? This cannot be undone.")) return;
    setStatus("running");
    try {
      const { data } = await api.post("/admin/cleanup-legacy-admins");
      setResult(data);
      setStatus("done");
    } catch (err) {
      setResult({ error: formatApiErrorDetail(err.response?.data?.detail) || err.message });
      setStatus("error");
    }
  };

  return (
    <div className="bg-slate-900 border border-red-500/30 rounded-xl p-5" data-testid="legacy-admin-cleanup-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-white">Danger zone — legacy admin cleanup</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            One-time removal of the three legacy admin accounts that were replaced by the single{" "}
            <code className="text-slate-300">admin</code> login.
          </p>
        </div>
        <button
          type="button"
          data-testid="legacy-admin-cleanup-btn"
          onClick={run}
          disabled={status === "running"}
          className="bg-red-500 hover:bg-red-400 text-white font-bold text-xs uppercase tracking-wide px-4 py-2 rounded-md disabled:opacity-50"
        >
          {status === "running" ? "Removing…" : "Remove legacy admins"}
        </button>
      </div>
      {status === "done" && result && (
        <div className="mt-3 text-xs text-emerald-400 font-mono" data-testid="legacy-admin-cleanup-result">
          Removed {result.deleted} account{result.deleted === 1 ? "" : "s"}.
        </div>
      )}
      {status === "error" && (
        <div className="mt-3 text-xs text-red-400 font-mono">
          Error: {result?.error || "Failed to run cleanup"}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, views, uniques, accent }) {
  const isYellow = accent === "yellow";
  return (
    <div className={`bg-slate-900 border rounded-xl p-5 ${isYellow ? "border-yellow-400/40 shadow-[0_0_20px_rgba(250,204,21,0.08)]" : "border-slate-800"}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
      <div className={`font-mono text-3xl font-black tabular-nums ${isYellow ? "text-yellow-400" : "text-white"}`}>{views}</div>
      <div className="text-xs text-slate-500 mt-1">{uniques} unique visitor{uniques === 1 ? "" : "s"}</div>
    </div>
  );
}

// ---------- Inline price input (commits on blur or Enter) ----------
function PriceInput({ value, onSave, testId, readOnly = false }) {
  const [v, setV] = React.useState(String(value ?? 0));
  React.useEffect(() => { setV(String(value ?? 0)); }, [value]);
  const commit = () => {
    const n = parseFloat(v);
    if (!isNaN(n) && n !== Number(value || 0)) onSave(n);
  };
  if (readOnly) {
    return (
      <span data-testid={testId} className="inline-block w-20 px-2 py-1 text-xs text-slate-300 font-mono tabular-nums">
        ${Number(value || 0).toFixed(2)}
      </span>
    );
  }
  return (
    <input
      type="number"
      step="0.01"
      min="0"
      data-testid={testId}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
      className="w-20 bg-slate-950 border border-slate-800 rounded px-2 py-1 text-xs text-white font-mono focus:outline-none focus:border-yellow-400"
    />
  );
}

// ---------- helpers ----------
const input = "w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-yellow-400";

function IconBtn({ children, onClick, danger, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`p-1.5 rounded ${danger ? "text-red-400 hover:bg-red-500/10" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
    >
      {children}
    </button>
  );
}
